import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { firestore } from '@/utils/firebase';
import {
	collection,
	updateDoc,
	where,
	orderBy,
	doc,
	getDoc,
	deleteDoc,
	writeBatch,
	FieldValue,
	addDoc,
	setDoc,
	runTransaction,
} from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import {
	convertFirestampToISOString,
	DEAL_STATUS_TYPES,
	delay,
	formatCurrency,
	formatDate,
	isFirebaseId,
} from '@/utils/constants';
import BASE_QUERY, { authBaseQuery } from '@/features/baseQuery';
import { clientsApi } from '../clients/clientsApi';
import { engagementsApi } from '../engagements/engagementsApi';
import { ticketsApi } from '../claims/ticketsApi';
import { CLIENT_TYPE } from '@/components/forms/CreateNewBusinessForm/validationSchema';
import { getApi } from '../apis';
import { triggerRefresh } from '../table/dealsTableSlice';
import { IS_DEV } from '@/utils/environment';
import { getActingUser } from '../user/userApi';
import { chunk } from 'lodash';
import testDealJson from '@/assets/json/test-deal.json';
import {
	createArchivedActivity,
	createdClosedActivity,
	createNotification,
	createDealAssignedNotification,
	DEALS_COLLECTION,
	getDealById,
	checkDealAccess,
} from './helper';
import { queryByBrokerGroup, queryViewableRecords } from '@/utils/firebaseService';
import { policiesApi } from '../policies/policiesApi';
import { retrieveDealPolicyChunks } from './dealPolicyCache';
import { buildTypesenseInFilter, TYPESENSE_CHUNK_SIZE } from '@/utils/typesense/util';

export const getDealFromCache = (state, dealId) => {
	const cached = dealsApi.endpoints.getDealRow.select(dealId)(state)?.data;
	return cached;
};

export const upsertDealRowFields = (api, dispatch, getState, dealId, fields) => {
	if (!dealId || !fields) return;

	const existing = api.endpoints.getDealRow.select(dealId)(getState())?.data;
	dispatch(
		api.util.upsertQueryData('getDealRow', dealId, {
			...(existing ?? { dealId: dealId }),
			...fields,
		}),
	);
};

const RTK_CHUNK_SIZE = 20;
// Max docIds per /v1/typesense-sync call (the backend's MAX_PER_API_BATCH_SIZE).
const TYPESENSE_SYNC_BATCH_SIZE = 50;

const getDealsFromCache = (state, dealIds) => {
	const dataByDealId = new Map();

	for (const dealId of dealIds) {
		const id = dealId?.toString();
		const cachedData = getDealFromCache(state, id);
		if (cachedData) dataByDealId.set(id, cachedData);
	}

	return dataByDealId;
};

const getDealsFromTypesense = async (dealIds, dispatch) => {
	const dataByDealId = new Map();

	for (const idChunk of chunk(dealIds, TYPESENSE_CHUNK_SIZE)) {
		// Callers may pass deal dealIds or deal doc ids (e.g. activities store the doc
		// id), so match either and key each row by whichever id was requested.
		const requested = new Set(idChunk.map(String));
		const rows = await dispatch(
			dealsApi.endpoints.listDeals.initiate({
				filters: `${buildTypesenseInFilter('dealId', idChunk)} || ${buildTypesenseInFilter('id', idChunk)}`,
				// includeFields: ['hubspotId', 'name'],
			}),
		).unwrap();

		for (const row of rows) {
			[row.dealId, row.id]
				.map((id) => id?.toString())
				.filter((id) => id && requested.has(id))
				.forEach((id) => dataByDealId.set(id, row));
		}
	}

	return dataByDealId;
};

const getDealsFromRtk = async (dealIds, dispatch) => {
	const dataByDealId = new Map();

	for (const idChunk of chunk(dealIds, RTK_CHUNK_SIZE)) {
		const results = await Promise.all(
			idChunk.map(async (dealId) => {
				try {
					const data = await dispatch(
						dealsApi.endpoints.getDealRow.initiate(dealId, {
							subscribe: false,
						}),
					).unwrap();

					return [dealId, data ?? null];
				} catch {
					return [dealId, null];
				}
			}),
		);

		for (const [dealId, data] of results) {
			if (data) dataByDealId.set(dealId, data);
		}
	}

	return dataByDealId;
};

export const dealsApi = createApi({
	reducerPath: 'dealsApi',
	tagTypes: [
		'DEALS',
		'DEAL',
		'DEAL_POLICIES',
		'DEAL_ROW',
		'CLIENT_DEALS',
		'POLICY_TRANSACTIONS',
		'DEBTOR_AUTOMATION',
		'DEBTOR_REMINDERS',
		'PORTFOLIO_STATEMENTS',
		'ENGAGE_SHARE_LINK',
		'ENGAGE_REPORTS',
	],
	baseQuery: authBaseQuery,
	endpoints: (builder) => ({
		// Defining an endpoint to get properties for specific deal
		getDealRow: builder.query({
			// Tagged by doc id, so the copy cached under the HubSpot id is invalidated too.
			providesTags: (result, error, dealId) => {
				return [{ type: 'DEAL_ROW', id: result?.id ?? dealId }];
			},
			// Also cache the deal under its other id (doc id or HubSpot id), so the
			// old-link redirect doesn't fetch it again.
			onQueryStarted: async (dealId, { dispatch, getState, queryFulfilled }) => {
				try {
					const { data } = await queryFulfilled;
					const otherId = isFirebaseId(dealId) ? data?.dealHubspotId : data?.id;
					if (!otherId) return;
					const cached = dealsApi.endpoints.getDealRow.select(otherId)(getState())?.data;
					if (!cached) {
						dispatch(dealsApi.util.upsertQueryData('getDealRow', otherId, data));
					}
				} catch (error) {
					console.log('Error caching deal row under its other id', error);
				}
			},
			queryFn: async (dealId, { getState, dispatch }) => {
				const userData = await getActingUser(dispatch, getState);

				const brokerGroupArray = userData?.brokerGroupArray ?? [];

				if (dealId === testDealJson?.id) {
					return {
						data: {
							...testDealJson,
							brokerGroupId: userData?.brokerGroupId,
							ownerId: userData?.hubspotId,
							ownerDocId: userData?.userId,
							isClaim: false,
						},
					};
				}

				const isHubspotDealId = /^\d+$/.test(dealId.toString());
				let docData;
				if (isHubspotDealId) {
					docData = await checkDealAccess(dealId, userData);
				} else {
					const docRef = doc(firestore, DEALS_COLLECTION, dealId.toString());
					const docSnap = await getDoc(docRef);
					docData = docSnap.exists() ? docSnap : null;
				}

				if (!docData) {
					return { data: null };
				}

				let brokerGroup;
				if (docData?.data()?.brokerGroup) {
					brokerGroup = {
						...JSON.parse(JSON.stringify(docData.data().brokerGroup)),
						path: docData.data().brokerGroup?.path,
					};
				}
				let previousDealRow;
				if (docData?.data()?.previousDealRow) {
					previousDealRow = {
						...JSON.parse(JSON.stringify(docData.data().previousDealRow)),
						path: docData.data().previousDealRow?.path,
					};
				}

				let nextDealRow;
				if (docData?.data()?.nextDealRow) {
					nextDealRow = {
						...JSON.parse(JSON.stringify(docData.data().nextDealRow)),
						path: docData.data().nextDealRow?.path,
					};
				}

				const ticketPipelines = [
					process.env.REACT_APP_PIPELINE_CLAIMS,
					...(IS_DEV ? [process.env.REACT_APP_PIPELINE_ENDORSEMENTS] : []),
				];
				const objectType = ticketPipelines.includes(docData?.data()?.pipeline)
					? 'ticket'
					: docData?.data()?.pipeline === process.env.REACT_APP_PIPELINE_DEBTORS
						? 'debtor'
						: 'deal';
				const state = getState();

				const canViewDeal = brokerGroupArray.includes(docData?.data()?.brokerGroupId);

				const data = docData
					? (() => {
							const rawData = docData.data();

							let {
								estimatedCloseDate,
								effectiveDate,
								nextTaskDate,
								lastUpdated,
								createDate,
								...rest
							} = rawData;

							estimatedCloseDate = convertFirestampToISOString(estimatedCloseDate);
							effectiveDate = convertFirestampToISOString(effectiveDate);
							nextTaskDate = convertFirestampToISOString(nextTaskDate);
							lastUpdated = convertFirestampToISOString(lastUpdated);
							createDate = convertFirestampToISOString(createDate);

							return {
								id: docData.id,
								...JSON.parse(JSON.stringify(rest)),
								estimatedCloseDate,
								nextTaskDate,
								effectiveDate,
								lastUpdated,
								createDate,
								brokerGroup,
								nextDealRow,
								previousDealRow,
								objectType,
								canViewDeal,
								isClaim:
									rawData?.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS,
							};
						})()
					: null;

				return { data };
			},
		}),
		// Through the deal API, so everyone who can view the deal sees its active links
		// (including the client's owner outside the deal's broker group).
		getFolioEngageShareLinks: builder.query({
			providesTags: (result, error, dealId) => [{ type: 'ENGAGE_SHARE_LINK', id: dealId }],
			query: (dealId) => ({
				url: `${process.env.REACT_APP_DEAL_API}/${dealId}/engageShareLinks/list`,
				method: 'POST',
				body: { dealId },
			}),
			transformResponse: (response) => response?.shareLinks ?? [],
		}),
		// The deal's Folio Engage report runs, newest first. engageReports has no Firestore
		// rule for app users, so it's read through the deal API.
		getEngageReports: builder.query({
			providesTags: (result, error, dealId) => [{ type: 'ENGAGE_REPORTS', id: dealId }],
			query: (dealId) => ({
				url: `${process.env.REACT_APP_DEAL_API}/${dealId}/engageReports/list`,
				method: 'POST',
				body: { dealId },
			}),
			transformResponse: (response) => response?.engageReports ?? [],
		}),
		generateEngageReport: builder.mutation({
			invalidatesTags: (result, error, { dealId }) => [
				{ type: 'ENGAGE_REPORTS', id: dealId },
			],
			query: ({ dealId, reportType, aiMessageId }) => ({
				url: `${process.env.REACT_APP_DEAL_API}/${dealId}/engageReports/generate`,
				method: 'POST',
				body: { reportType, aiMessageId },
			}),
		}),
		// From the SharePoint retry banner: `complete` once saved, or `dismissed`
		updateEngageReport: builder.mutation({
			invalidatesTags: (result, error, { dealId }) => [
				{ type: 'ENGAGE_REPORTS', id: dealId },
			],
			query: ({ dealId, docId, status }) => ({
				url: `${process.env.REACT_APP_DEAL_API}/${dealId}/engageReports/update`,
				method: 'POST',
				body: { docId, status },
			}),
		}),
		getDealsByClientId: builder.query({
			queryFn: async (clientId, { dispatch, getState }) => {
				if (!clientId) return [];
				const userData = await getActingUser(dispatch, getState);

				const deals = await queryViewableRecords({
					collectionRef: collection(firestore, 'deals'),
					constraints: [where('clientDocId', '==', clientId.toString())],
					user: userData,
					ownerFields: ['ownerDocId', 'clientOwnerDocId'],
				});

				return {
					data: deals.map((d) => {
						return {
							id: d.id,
							...JSON.parse(JSON.stringify(d.data())),
						};
					}),
				};
			},
		}),
		addPoliciesToDeal: builder.mutation({
			invalidatesTags: (result, error, { dealId, clientId }) => [
				{ type: 'DEAL_POLICIES', id: dealId },
				{ type: 'DEAL', id: dealId },
				{ type: 'DEALS', id: 'LIST' },
				{ type: 'POLICY_TRANSACTIONS', id: dealId },
				{ type: 'DEBTOR_AUTOMATION', id: dealId },
				{ type: 'CLIENT_POLICIES', id: clientId },
				{ type: 'CLIENT', id: clientId },
				{ type: 'DEAL_ROW', id: dealId },
				'CLIENT_DEALS',
			],
			query: ({ dealId, policies, uploadPolicyDocs }) => ({
				url: process.env.REACT_APP_CREATE_POLICY_ROW,
				method: 'POST',
				body: { dealId, policies, uploadPolicyDocs },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform addPoliciesToDeal error response', response);
				return response;
			},
			transformResponse: (response, meta) => {
				console.log('👾 ~ transform addPoliciesToDeal response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch(
						clientsApi.util.invalidateTags([
							{ type: 'CLIENTS', id: 'LIST' },
							{ type: 'CLIENT', id: arg.clientId },
							{ type: 'CLIENT_POLICIES', id: arg.clientId },
						]),
					);
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},

			// queryFn: async ({ dealId, docId, policies }) => {
			//  try {
			//      const collectionRef = collection(firestore, 'deals');
			//      const ref = doc(collectionRef, docId);
			//      const docSnapshot = await getDoc(ref);
			//      const currentPolicies = docSnapshot.data().policies || [];
			//      const updatedPolicies = currentPolicies.concat(policies);
			//      await updateDoc(ref, { policies: updatedPolicies });

			//      return { data: updatedPolicies };
			//  } catch (error) {
			//      return { error: { status: 'CUSTOM_ERROR', error: error.message } };
			//  }
			// },
		}),
		removeDealPolicy: builder.mutation({
			invalidatesTags: (result, error, { dealId, clientId }) => [
				{ type: 'DEAL_POLICIES', id: dealId },
				{ type: 'DEAL', id: dealId },
				{ type: 'DEALS', id: 'LIST' },
				{ type: 'POLICY_TRANSACTIONS', id: dealId },
				{ type: 'DEBTOR_AUTOMATION', id: dealId },
				{ type: 'CLIENT_POLICIES', id: clientId },
				{ type: 'CLIENT', id: clientId },
				{ type: 'DEAL_ROW', id: dealId },
				'CLIENT_DEALS',
			],
			query: ({ dealId, policyId, policyTransactionId }) => ({
				url: process.env.REACT_APP_REMOVE_POLICY_ROW,
				method: 'POST',
				body: { dealId, policyId, policyTransactionId },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform removeDealPolicy error response', response);
				return response;
			},
			transformResponse: (response, meta) => {
				console.log('👾 ~ transform removeDealPolicy response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch(
						clientsApi.util.invalidateTags([
							{ type: 'CLIENTS', id: 'LIST' },
							{ type: 'CLIENT', id: arg.clientId },
							{ type: 'CLIENT_POLICIES', id: arg.clientId },
						]),
					);
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		updateDealPolicyTransaction: builder.mutation({
			invalidatesTags: (
				result,
				error,
				{ dealId, policyTransactionDocId, clientId, currentDealId },
			) => [
				{ type: 'DEAL_POLICIES', id: dealId },
				{ type: 'DEAL_POLICIES', id: currentDealId },
				{ type: 'DEAL', id: dealId },
				{ type: 'DEAL', id: currentDealId },
				{ type: 'DEALS', id: 'LIST' },
				{ type: 'DEBTOR_AUTOMATION', id: dealId },
				{ type: 'DEBTOR_AUTOMATION', id: currentDealId },
				{ type: 'CLIENT_POLICIES', id: clientId },
				{ type: 'CLIENT', id: clientId },
				{ type: 'DEAL_ROW', id: dealId },
				'CLIENT_DEALS',
			],
			query: ({ dealId, policyTransactionDocId, clientId, currentDealId }) => ({
				url: process.env.REACT_APP_UPDATE_POLICY_ROW,
				method: 'POST',
				body: { dealId, policyTransactionDocId, currentDealId },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform updateDealPolicy error response', response);
				return response;
			},
			transformResponse: (response, meta) => {
				console.log('👾 ~ transform updateDealPolicy response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch(
						clientsApi.util.invalidateTags([
							{ type: 'CLIENTS', id: 'LIST' },
							{ type: 'CLIENT', id: arg.clientId },
							{ type: 'CLIENT_POLICIES', id: arg.clientId },
						]),
					);
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		updateDealPolicies: builder.mutation({
			invalidatesTags: (result, error, { dealId, clientId }) => [
				{ type: 'DEAL_POLICIES', id: dealId },
				{ type: 'DEAL', id: dealId },
				{ type: 'DEALS', id: 'LIST' },
				{ type: 'CLIENT_POLICIES', id: clientId },
				{ type: 'CLIENT', id: clientId },
				{ type: 'DEAL_ROW', id: dealId },
				'CLIENT_DEALS',
			],
			query: ({ dealId, clientId }) => ({
				url: process.env.REACT_APP_UPDATE_DEAL_POLICIES,
				method: 'POST',
				body: { dealId },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform updateDealPolicies error response', response);
				return response;
			},
			transformResponse: (response, meta) => {
				console.log('👾 ~ transform updateDealPolicies response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch(
						clientsApi.util.invalidateTags([
							{ type: 'CLIENTS', id: 'LIST' },
							{ type: 'CLIENT', id: arg.clientId },
							{ type: 'CLIENT_POLICIES', id: arg.clientId },
						]),
					);
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		getDealPolicies: builder.query({
			queryFn: async (
				{ dealId, includeTransactions = true, showLatest = false },
				{ dispatch },
			) => {
				const data = await dispatch(
					dealsApi.endpoints.getDealListPoliciesByDealId.initiate({
						dealId: dealId,
						includeTransactions,
						showLatest,
					}),
				).unwrap();
				// console.log('getDealPolicies', data);
				const policies = data?.map((p) => p.policy);
				const transactions = data
					?.map((p) => p.transaction)
					.filter((t) => t && Object.keys(t).length > 0);
				// console.log('dealPolicies', { policies, transactions });
				return {
					data: { policies, transactions },
				};
			},
			providesTags: (result, error, { dealId }) => [
				{
					type: 'DEAL_POLICIES',
					id: dealId,
				},
			],
			// providesTags: (result, error, { dealId }) => {
			// 	return [{ type: 'DEAL', id: dealId }];
			// },
			// query: ({ dealId }) => ({
			// 	url: process.env.REACT_APP_RETRIEVE_ALL_DEAL_POLICIES,
			// 	method: 'POST',
			// 	body: { dealId },
			// }),
			// transformErrorResponse: (response) => {
			// 	console.log('👾 ~ transform getDealPolicies error response', response);
			// 	return response;
			// },
			// transformResponse: (response) => {
			// 	console.log('👾 ~ transform getDealPolicies response', response);
			// 	return response;
			// },
		}),

		getDealPoliciesChunk: builder.query({
			providesTags: (result, error, { dealIds }) => {
				return dealIds.map((dealId) => ({ type: 'DEAL_POLICIES', id: dealId }));
			},
			query: ({ dealIds, includeTransactions = true, showLatest = false }) => ({
				url: process.env.REACT_APP_BATCH_GET_POLICIES_BY_DEAL_IDS,
				method: 'POST',
				body: { dealIds, includeTransactions, showLatest },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform getDealPoliciesChunk error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform getDealPoliciesChunk response', response);
				return response;
			},
		}),
		getDealListPoliciesByDealId: builder.query({
			queryFn: async (
				{ dealId, includeTransactions = true, showLatest = false },
				{ dispatch },
			) => {
				const data = await dispatch(
					dealsApi.endpoints.getDealPoliciesChunk.initiate({
						dealIds: [dealId],
						includeTransactions,
						showLatest,
					}),
				).unwrap();
				return {
					data: data?.[dealId],
				};
			},
			providesTags: (result, error, { dealId }) => [
				{
					type: 'DEAL_POLICIES',
					id: dealId,
				},
			],
		}),
		batchGetDealListPolicies: builder.query({
			providesTags: (result, error, { dealIds }) => {
				return dealIds.map((dealId) => ({ type: 'DEAL_POLICIES', id: dealId }));
			},
			queryFn: async (
				{
					dealIds,
					includeTransactions = true,
					showLatest = false,
					waitForRetrieval = false,
				},
				{ dispatch, getState },
			) => {
				const state = getState();
				const policiesByDealId = {};
				const statusByDealId = {};
				const errorByDealId = {};

				const missingDealIds = [];

				for (const dealId of dealIds) {
					const cacheArg = { dealId, includeTransactions, showLatest };
					const cachedQuery =
						dealsApi.endpoints.getDealListPoliciesByDealId.select(cacheArg)(state);

					if (cachedQuery.data) {
						policiesByDealId[dealId] = cachedQuery.data;
						statusByDealId[dealId] = 'fulfilled';
					} else {
						statusByDealId[dealId] = 'loading';
						missingDealIds.push(dealId);
					}
				}
				const data = {
					dealIds,
					statusByDealId,
					policiesByDealId,
					errorByDealId,
				};

				/*
				 * Return cached values immediately and let onQueryStarted retrieve
				 * the missing values progressively.
				 */
				if (!waitForRetrieval || missingDealIds.length === 0) {
					return { data };
				}

				await retrieveDealPolicyChunks({
					dealIds: missingDealIds,
					includeTransactions,
					dispatch,
					dealsApi,
					onChunkSuccess: ({ dealIdChunk, resultByDealId }) => {
						for (const dealId of dealIdChunk) {
							if (Object.prototype.hasOwnProperty.call(resultByDealId, dealId)) {
								policiesByDealId[dealId] = resultByDealId[dealId];

								statusByDealId[dealId] = 'fulfilled';
								delete errorByDealId[dealId];
							} else {
								statusByDealId[dealId] = 'rejected';

								errorByDealId[dealId] = 'No result was returned for this deal';
							}
						}
					},

					onChunkError: ({ dealIdChunk, error }) => {
						const errorMessage =
							error?.data?.message ??
							error?.data?.error ??
							error?.error ??
							error?.message ??
							'Failed to retrieve policies';

						for (const dealId of dealIdChunk) {
							statusByDealId[dealId] = 'rejected';
							errorByDealId[dealId] = errorMessage;
						}
					},
				});

				return { data };
			},
			onQueryStarted: async (
				{
					dealIds,
					includeTransactions = true,
					showLatest = false,
					waitForRetrieval = false,
				},
				{ dispatch, getState, queryFulfilled, updateCachedData },
			) => {
				if (waitForRetrieval) return;

				try {
					// Wait until the initial cached result exists.
					await queryFulfilled;
				} catch {
					return;
				}

				const state = getState();
				const missingDealIds = dealIds.filter((dealId) => {
					const cachedQuery = dealsApi.endpoints.getDealListPoliciesByDealId.select({
						dealId,
						includeTransactions,
						showLatest,
					})(state);

					return cachedQuery.data === undefined;
				});

				if (missingDealIds.length === 0) return;

				await retrieveDealPolicyChunks({
					dealIds: missingDealIds,
					includeTransactions,
					showLatest,
					dispatch,
					dealsApi,
					onChunkSuccess: ({ dealIdChunk, resultByDealId }) => {
						updateCachedData((draft) => {
							for (const dealId of dealIdChunk) {
								if (Object.prototype.hasOwnProperty.call(resultByDealId, dealId)) {
									draft.policiesByDealId[dealId] = resultByDealId[dealId];

									draft.statusByDealId[dealId] = 'fulfilled';

									delete draft.errorByDealId[dealId];
								} else {
									draft.statusByDealId[dealId] = 'rejected';

									draft.errorByDealId[dealId] =
										'No result was returned for this deal';
								}
							}
						});
					},
					onChunkError: ({ dealIdChunk, error }) => {
						const errorMessage =
							error?.data?.message ??
							error?.data?.error ??
							error?.error ??
							error?.message ??
							'Failed to retrieve policies';

						updateCachedData((draft) => {
							for (const dealId of dealIdChunk) {
								draft.statusByDealId[dealId] = 'rejected';

								draft.errorByDealId[dealId] = errorMessage;
							}
						});
					},
				});
			},
			// query: ({ dealIds, includeTransactions = true }) => ({
			// 	url: process.env.REACT_APP_BATCH_GET_POLICIES_BY_DEAL_IDS,
			// 	method: 'POST',
			// 	body: { dealIds, includeTransactions },
			// }),
			// transformErrorResponse: (response) => {
			// 	console.log('👾 ~ transform batchGetDealListPolicies error response', response);
			// 	return response;
			// },
			// transformResponse: (response) => {
			// 	console.log('👾 ~ transform batchGetDealListPolicies response', response);
			// 	return response;
			// },
		}),

		getDealPolicyTransactions: builder.query({
			providesTags: (result, error, { dealId }) => [
				{ type: 'POLICY_TRANSACTIONS', id: dealId },
			],
			// transaction type can be quotation, newBusiness, endorsement, cancellation, renewal, reinstatement
			query: ({ policyId, transactionType, dealId, showMultipleTypes }) => ({
				url: process.env.REACT_APP_RETRIEVE_POLICY_TRANSACTIONS,
				method: 'POST',
				body: { policyId, transactionType, dealId, showMultipleTypes },
			}),
			// queryFn: async ({ policyId, transactionType, dealId }, { getState }, extraOptions, baseQuery) => {

			// 	const response = await baseQuery({
			// 		url: process.env.REACT_APP_RETRIEVE_POLICY_TRANSACTIONS,
			// 		method: 'POST',
			// 		body: { policyId, transactionType, dealId },
			// 	});
			// 	// Return the response
			// 	if (response.error) {
			// 		return { error: response.error };
			// 	}

			// 	console.log('👾 ~ transform getDealPolicyTransaction response', response.data);
			// 	return { data: response.data };
			// },
			transformResponse: (response) => {
				console.log('👾 ~ transform getDealPolicyTransaction response', response);
				return response;
			},
		}),
		createNewBusiness: builder.mutation({
			invalidatesTags: (result, error, arg) => [{ type: 'DEALS', id: 'LIST' }],
			query: ({
				completedBy,
				dealName,
				notes,
				adviserFee,
				renewalDate,
				clientId,
				clientGoals,
				clientCategory,
				clientType,
				needsAnalysis,
				additionalProducts,
				riskClasses,
				additionalGoals,
				brokerGroupId,
				...params
			}) => ({
				url: `${process.env.REACT_APP_DEAL_API}/create/new-business`,
				method: 'POST',
				body: {
					completedBy,
					dealName,
					notes,
					adviserFee,
					renewalDate,
					clientId,
					clientGoals,
					clientCategory,
					clientType,
					needsAnalysis,
					additionalProducts,
					riskClasses,
					additionalGoals,
					brokerGroupId,
					...params,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform createNewBusiness response', response);
				return response;
			},
			onQueryStarted: async ({ ownerDocId }, { dispatch, getState, queryFulfilled }) => {
				try {
					const res = await queryFulfilled;
					const dealId = res.data?.dealId;
					if (dealId) {
						await createDealAssignedNotification({ dispatch, getState, dealId, assigneeDocId: ownerDocId });
					}
				} catch (e) {
					//
				}
			},
		}),
		createEndorsement: builder.mutation({
			// This tag indicates that this mutation invalidates the list of clients
			invalidatesTags: (result, error, arg) => [
				{ type: 'DEALS', id: 'LIST' },
				'CLIENT_DEALS',
			],
			query: ({
				dealName,
				notes,
				policies,
				clientId,
				completedBy,
				brokerGroupId,
				...params
			}) => ({
				url: `${process.env.REACT_APP_DEAL_API}/create/endorsement`,
				method: 'POST',
				body: {
					dealName,
					notes,
					policies,
					clientId,
					completedBy,
					brokerGroupId,
					...params,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform create Endorsement response', response);
				return response;
			},
			onQueryStarted: async ({ ownerDocId }, { dispatch, getState, queryFulfilled }) => {
				try {
					const res = await queryFulfilled;
					const dealId = res.data?.dealId;
					if (dealId) {
						await createDealAssignedNotification({ dispatch, getState, dealId, assigneeDocId: ownerDocId });
					}
				} catch (e) {
					//
				}
			},
		}),
		createRenewal: builder.mutation({
			// This tag indicates that this mutation invalidates the list of clients
			invalidatesTags: (result, error, arg) => [
				{ type: 'DEALS', id: 'LIST' },
				'CLIENT_DEALS',
			],
			query: ({
				dealName,
				notes,
				clientId,
				renewalDate,

				policyIds,
				adviserFee,
				clientGoals,
				additionalGoals,
				clientCategory,
				clientType,
				needsAnalysis,
				additionalProducts,
				riskClasses,
				previousDealId,
				previousDealIds = [],
				brokerGroupId,
				...params
			}) => ({
				url: `${process.env.REACT_APP_DEAL_API}/create/renewal`,
				method: 'POST',
				body: {
					dealName,
					notes,
					clientId,
					renewalDate,

					previousDealIds: Array.from(
						new Set([...(previousDealId ? [previousDealId] : []), ...previousDealIds]),
					),
					policyIds,
					adviserFee,
					clientGoals,
					additionalGoals,
					clientCategory,
					clientType,
					needsAnalysis,
					additionalProducts,
					riskClasses,
					brokerGroupId,
					...params,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform create Renewal response', response);
				return response;
			},
			onQueryStarted: async ({ ownerDocId }, { dispatch, getState, queryFulfilled }) => {
				try {
					const res = await queryFulfilled;
					const dealId = res.data?.dealId;
					if (dealId) {
						await createDealAssignedNotification({ dispatch, getState, dealId, assigneeDocId: ownerDocId });
					}
				} catch (e) {
					//
				}
			},
		}),
		previewBulkRenewals: builder.mutation({
			queryFn: async (
				{ toDateFrom, toDateTo, branchId, ownerDocIds, pageLimit, groupType },
				{ dispatch },
			) => {
				try {
					const preview = await dispatch(
						dealsApi.endpoints.createBulkRenewals.initiate({
							preview: true,
							toDateFrom,
							toDateTo,
							branchId,
							ownerDocIds,
							pageLimit,
							groupType,
						}),
					).unwrap();

					return { data: preview };
				} catch (e) {
					console.error('👾 ~ preview bulk renewals error', e);

					return {
						error: {
							status: e?.status ?? 'CUSTOM_ERROR',
							data: e?.data ?? e?.message ?? 'Preview bulk renewals failed',
						},
					};
				}
			},
		}),
		createBulkRenewals: builder.mutation({
			invalidatesTags: () => [{ type: 'DEALS', id: 'LIST' }, 'CLIENT_DEALS'],
			query: ({
				preview = false,
				toDateFrom,
				toDateTo,
				branchId,
				ownerDocIds,
				pageLimit,
				groupType,
				portfolioTemplateId,
			}) => ({
				url: `${process.env.REACT_APP_DEAL_API}/create/renewal/batch`,
				method: 'POST',
				body: {
					preview,
					toDateFrom,
					toDateTo,
					branchId,
					ownerDocIds,
					pageLimit,
					groupType,
					portfolioTemplateId,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform create bulk renewals response', response);
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform create bulk renewals error response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					if (!arg.preview) {
						dispatch(
							policiesApi.util.invalidateTags(['POLICIES', 'POLICY_TRANSACTIONS']),
						);
					}
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		batchUpdateDeals: builder.mutation({
			query: ({ dealIds, properties }) => ({
				url: `${process.env.REACT_APP_DEAL_API}/update/batch`,
				method: 'POST',
				body: { dealIds, properties },
			}),
			transformResponse: (response) => response,
			invalidatesTags: (result, error, { dealIds }) => [
				{ type: 'DEALS', id: 'LIST' },
				...dealIds.map((dealId) => ({ type: 'DEAL_ROW', id: dealId })),
				'CLIENT_DEALS',
			],
			onQueryStarted: async ({ properties }, { dispatch, queryFulfilled, getState }) => {
				try {
					const { data } = await queryFulfilled;
					const updated = data?.updated ?? [];

					for (const { dealId } of updated) {
						upsertDealRowFields(dealsApi, dispatch, getState, dealId, properties);
					}

					if (updated.length > 0) {
						dispatch(triggerRefresh());
					}
				} catch (error) {
					console.log('Error syncing bulk deal update', error);
				}
			},
		}),
		previewBulkDebtors: builder.mutation({
			queryFn: async (
				{
					expiryFromDate,
					expiryToDate,
					authRepId,
					ownerDocIds,
					fundingStatus,
					effectiveFromDate,
					effectiveToDate,
				},
				{ dispatch },
			) => {
				try {
					const preview = await dispatch(
						dealsApi.endpoints.createBulkDebtors.initiate({
							preview: true,
							expiryFromDate,
							expiryToDate,
							authRepId,
							ownerDocIds,
							fundingStatus,
							effectiveFromDate,
							effectiveToDate,
						}),
					).unwrap();

					return { data: preview };
				} catch (e) {
					console.error('👾 ~ preview bulk debtors error', e);

					return {
						error: {
							status: e?.status ?? 'CUSTOM_ERROR',
							data: e?.data ?? e?.message ?? 'Preview bulk debtors failed',
						},
					};
				}
			},
		}),
		createBulkDebtors: builder.mutation({
			invalidatesTags: () => [{ type: 'DEALS', id: 'LIST' }, 'CLIENT_DEALS'],
			query: ({
				preview = false,
				expiryFromDate,
				expiryToDate,
				authRepId,
				ownerDocIds,
				fundingStatus,
				effectiveFromDate,
				effectiveToDate,
			}) => ({
				url: `${process.env.REACT_APP_DEBTOR_API}/batch/create`,
				method: 'POST',
				body: {
					preview,
					expiryFromDate,
					expiryToDate,
					authRepId,
					ownerDocIds,
					fundingStatus,
					effectiveFromDate,
					effectiveToDate,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform create bulk debtors response', response);
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform create bulk debtors error response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					if (!arg.preview) {
						dispatch(
							policiesApi.util.invalidateTags(['POLICIES', 'POLICY_TRANSACTIONS']),
						);
					}
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		updateDealRow: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{ type: 'DEALS', id: 'LIST' },
				{ type: 'DEAL_ROW', id: arg.dealId },
				'CLIENT_DEALS',
			],
			queryFn: async (
				{ dealId, docId, properties, clientId },
				{ getState, dispatch },
				extraOptions,
				baseQuery,
			) => {
				const patchResult = dispatch(
					dealsApi.util.updateQueryData('getDealRow', dealId, (draft) => {
						if (draft) {
							Object.assign(draft, properties);
							if (properties?.estimatedCloseDate != null) {
								draft.estimatedCloseDate = convertFirestampToISOString(
									properties.estimatedCloseDate,
								);
							}
						}
					}),
				);
				try {
					const collectionRef = collection(firestore, DEALS_COLLECTION);

					const userData = await getActingUser(dispatch, getState);

					let ref;
					let dealData;
					if (docId) {
						ref = doc(collectionRef, docId);
						const snapshot = await getDoc(ref);
						dealData = snapshot.exists()
							? { id: snapshot.id, ref, ...snapshot.data() }
							: null;
					} else {
						const dealRef = await getDealById(dealId, userData);
						ref = dealRef?.ref;
						dealData = dealRef?.data();
					}
					//Change status to object type e.g debtor
					await updateDoc(ref, {
						...properties,
						...(Object.keys(properties).includes(CLIENT_TYPE) && {
							riskClasses: [],
							needsAnalysis: [],
						}),
					});

					// Best-effort immediate Typesense sync so search reflects this edit right
					// away instead of waiting on the ~1 minute batch flush. Not awaited/blocking
					// and errors are swallowed — the existing Firestore-trigger pipeline still
					// picks this doc up regardless if this call fails.
					baseQuery({
						url: `${process.env.REACT_APP_MISC_API_ENDPOINT}/v1/typesense-sync`,
						method: 'POST',
						body: { collection: 'deals', docId: ref.id },
					}).then((result) => {
						if (result?.error) {
							console.log('Error triggering immediate Typesense sync', result.error);
						}
					});

					try {
						//Properties status null is unarchived
						if (
							properties?.status === DEAL_STATUS_TYPES.archived ||
							properties?.status === null
						) {
							// Activities reference the deal by its Firestore doc id.
							await createArchivedActivity({
								dispatch,
								getState,
								properties,
								dealId: ref.id,
							});
						}
						//pipeline 0 claims only
						console.log('deal data: ', dealData);

						if (
							dealData?.pipeline === '0' &&
							(properties?.dateClosed === null || properties?.dateClosed)
						) {
							await createdClosedActivity({
								dispatch,
								getState,
								properties,
								dealId: ref.id,
							});
						}

						if (properties?.completed) {
							await createNotification({ dispatch, dealId, action: 'complete' });
						}

						if (
							properties?.ownerDocId &&
							dealData &&
							dealData.ownerDocId !== properties.ownerDocId
						) {
							await createDealAssignedNotification({
								dispatch,
								getState,
								dealId,
								assigneeDocId: properties.ownerDocId,
							});
						}
						try {
							if (dealData?.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS) {
								dispatch(
									ticketsApi.util.invalidateTags([
										{ type: 'TICKET', id: dealId },
									]),
								);
							}
						} catch (e) {
							console.log('Error invalidating deal tags', e);
						}

						dispatch(
							engagementsApi.util.invalidateTags([
								{ type: 'ENGAGEMENTS', id: dealId },
							]),
						);

						setTimeout(() => {
							dispatch(triggerRefresh());
						}, 1000);
					} catch (err) {
						console.log('Error invalidating tags', err);
					}
					//estimatedCloseDate is a firebase timestamp value...
					const serializableProps = { ...properties };
					if (properties?.estimatedCloseDate != null) {
						serializableProps.estimatedCloseDate = convertFirestampToISOString(
							properties.estimatedCloseDate,
						);
					}

					return { data: serializableProps };
				} catch (error) {
					patchResult.undo();
					return { error: { status: 'CUSTOM_ERROR', error: error.message } };
				}
			},
		}),
		batchUpdateDealRows: builder.mutation({
			invalidatesTags: (result, error, { deals }) => [
				{ type: 'DEALS', id: 'LIST' },
				...deals.map(({ dealId }) => ({ type: 'DEAL_ROW', id: dealId })),
				'CLIENT_DEALS',
			],
			queryFn: async ({ deals }, { dispatch, getState }, _extraOptions, baseQuery) => {
				// deals = [
				//     {
				//         dealId: 1234,
				//         properties: {
				//             status: 'Archived',
				//             isRenewable: false,
				//         }
				//     }
				// ]
				console.log('DEALS', deals);
				const dealIds = deals.map((d) => d.dealId);
				const patchResults = deals.map((deal) =>
					dispatch(
						dealsApi.util.updateQueryData('getDealRow', deal.dealId, (draft) => {
							if (draft) {
								Object.assign(draft, deal.properties);
							}
						}),
					),
				);

				try {
					const batch = writeBatch(firestore);
					const userData = await getActingUser(dispatch, getState);

					// const q = query(
					// 	collection(firestore, DEALS_COLLECTION),
					// 	where(
					// 		'dealId',
					// 		'in',
					// 		dealIds.flatMap((id) => [parseInt(id), id.toString()]),
					// 	),
					// 	where('brokerGroupId', 'in', brokerGroupArray),
					// );
					// const querySnapshot = await getDocs(q);

					// One read per deal (dealId is the doc id): the Firestore rule is checked
					// against each deal, and it avoids combining `dealId in` with
					// `brokerGroupId in`, which passes Firestore's 30-value limit for users in
					// 4+ broker groups.
					const snapshots = await Promise.all(
						Array.from(new Set(dealIds)).map((dealId) =>
							getDoc(doc(firestore, DEALS_COLLECTION, String(dealId))).catch(
								() => null,
							),
						),
					);
					const docs = snapshots.filter((snapshot) => snapshot?.exists());

					const dealRefs = docs.reduce((acc, doc) => {
						const deal = deals.find((d) => d.dealId == doc.data()?.dealId);
						acc.push({
							...deal,
							ref: doc.ref,
						});
						return acc;
					}, []);

					const pipelineByDealId = new Map(
						docs.map((doc) => [String(doc.data()?.dealId), doc.data()?.pipeline]),
					);

					console.log('DEAL REFS to update', dealRefs);

					dealRefs.forEach((deal) => {
						batch.update(deal.ref, {
							...deal.properties,
							...(Object.keys(deal.properties).includes(CLIENT_TYPE) && {
								riskClasses: [],
								needsAnalysis: [],
							}),
						});
					});

					console.log('BATCH', batch);
					await batch.commit();

					// Best-effort immediate Typesense sync, as in updateDealRow. Not awaited and
					// errors are swallowed — the Firestore-trigger pipeline still picks these
					// deals up within ~1 minute if this call fails.
					for (const docIds of chunk(
						dealRefs.map((deal) => deal.ref.id),
						TYPESENSE_SYNC_BATCH_SIZE,
					)) {
						baseQuery({
							url: `${process.env.REACT_APP_MISC_API_ENDPOINT}/v1/typesense-sync`,
							method: 'POST',
							body: { collection: 'deals', docIds },
						}).then((result) => {
							if (result?.error) {
								console.log(
									'Error triggering immediate Typesense sync',
									result.error,
								);
							}
						});
					}

					try {
						try {
							const { userId } = userData ?? {};

							const userRef = doc(firestore, 'user', userId);
							await Promise.all(
								deals
									.filter(
										(d) => d?.properties?.status === DEAL_STATUS_TYPES.archived,
									)
									.map(async ({ dealId }) => {
										const body = {
											activityType: 'dealArchived',
											dealId,
											completedBy: userRef,
											createdDate: Timestamp.now(),
										};
										console.log('BODY for activities', body);
										await addDoc(
											collection(firestore, 'activities'),
											body,
										).then((res) => {
											console.log(
												'Successfully created activity doc for deal update',
												dealId,
												res?.id,
											);
										});
									}),
							);
						} catch (err) {
							console.log('Error creating activity doc', err);
						}

						deals.forEach(({ dealId }) => {
							const pipeline = pipelineByDealId.get(String(dealId));
							if (pipeline === process.env.REACT_APP_PIPELINE_CLAIMS) {
								dispatch(
									ticketsApi.util.invalidateTags([
										{ type: 'TICKET', id: dealId },
									]),
								);
							}
							dispatch(
								engagementsApi.util.invalidateTags([
									{ type: 'ENGAGEMENTS', id: dealId },
								]),
							);
						});
					} catch (err) {
						console.log('Error invalidating tags', err);
					}
					setTimeout(async () => {
						console.log('TRIGGER REFRESH');
						dispatch(triggerRefresh());
					}, 1000);
					return { data: JSON.parse(JSON.stringify(dealRefs)) };
				} catch (error) {
					console.log('ERROR updating deal rows', error);
					patchResults.forEach((r) => r.undo());
					return { error: { status: 'CUSTOM_ERROR', error: error.message } };
				}
			},
		}),
		updateClientDealRows: builder.mutation({
			// This tag indicates that this mutation invalidates the list of clients
			invalidatesTags: (result, error, arg) => [
				{ type: 'DEALS', id: 'LIST' },
				'CLIENT_DEALS',
			],
			query: ({ clientId, newName }) => ({
				url: process.env.REACT_APP_UPDATE_CLIENT_DEAL_ROW,
				method: 'POST',
				body: {
					clientId,
					newName,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform updateClientDealRows response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch(
						clientsApi.util.invalidateTags([
							{ type: 'CLIENTS', id: 'LIST' },
							{ type: 'CLIENT', id: arg.clientId },
							{ type: 'CLIENT_POLICIES', id: arg.clientId },
						]),
					);
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		deleteDeal: builder.mutation({
			// This tag indicates that this mutation invalidates the list of clients
			invalidatesTags: () => ['DEALS', 'POLICY_TRANSACTIONS', 'CLIENT_DEALS'],
			// query: ({ dealId, objectType }) => ({
			// 	url: process.env.REACT_APP_DELETE_DEAL,
			// 	method: 'POST',
			// 	body: {
			// 		dealId,
			// 		objectType,
			// 	},
			// }),
			queryFn: async (
				{ dealId, objectType },
				{ getState, dispatch },
				extraOptions,
				baseQuery,
			) => {
				const uppercaseType = (objectType ?? '').toUpperCase();
				const userData = await getActingUser(dispatch, getState);

				const api = getApi(uppercaseType);
				try {
					// Works for every deal type: HubSpot-issued dealIds, HubSpot-less deals and
					// debtors (whose dealId is their doc id).
					const queryDoc = await getDealById(dealId, userData);
					if (!queryDoc) {
						throw new Error(`Deal document not found for dealId: ${dealId}`);
					}
					// Delete the HubSpot side first — if it fails, the Firestore record is
					// left intact so the delete can be retried from a consistent state.
					// Only deals that exist in HubSpot (they have a dealHubspotId) need it.
					if (queryDoc.data()?.dealHubspotId) {
						// Use the authenticated baseQuery (not raw axios) so this request
						// carries the Firebase token — the backend's canAccessDeal check
						// is skipped entirely for requests with no Authorization header.
						// The backend resolves the deal's HubSpot id from its doc id.
						const hubspotDeleteResult = await baseQuery({
							url: process.env.REACT_APP_DELETE_DEAL,
							method: 'POST',
							body: { dealId: queryDoc.id, objectType },
						});
						if (hubspotDeleteResult?.error) {
							throw new Error(
								hubspotDeleteResult.error?.data?.message ??
									hubspotDeleteResult.error?.message ??
									'Error deleting deal from Hubspot',
							);
						}
					}
					await deleteDoc(queryDoc.ref);

					// Best-effort immediate Typesense sync so the deleted deal disappears from
					// search right away instead of waiting on the ~1 minute batch flush. Not
					// awaited/blocking and errors are swallowed — the existing Firestore-trigger
					// pipeline still picks this up regardless if this call fails.
					baseQuery({
						url: `${process.env.REACT_APP_MISC_API_ENDPOINT}/v1/typesense-sync`,
						method: 'POST',
						body: { collection: 'deals', docId: queryDoc.id },
					}).then((result) => {
						if (result?.error) {
							console.log('Error triggering immediate Typesense sync', result.error);
						}
					});

					try {
						const { userId } = userData ?? {};

						const userRef = doc(firestore, 'user', userId);

						const body = {
							activityType: 'dealDeleted',
							deal: queryDoc.data(),
							// Activities reference the deal and client by their Firestore doc ids.
							...(queryDoc.data()?.clientDocId && {
								clientId: queryDoc.data().clientDocId,
							}),
							dealId: queryDoc.id,
							completedBy: userRef,
							createdDate: Timestamp.now(),
						};
						console.log('BODY for activities', body);
						await addDoc(collection(firestore, 'activities'), body).then((res) => {
							console.log(
								'Successfully created activity doc for deal deletion',
								dealId,
								res?.id,
							);
						});
						try {
							await setDoc(doc(firestore, 'deletedDeals', queryDoc.id), {
								...queryDoc.data(),
								dateDeleted: Timestamp.now(),
								...(userRef && { deletedBy: userRef }),
							}).then((res) => {
								console.log(
									'Successfully created deleted deal doc for deal deletion',
									dealId,
									res?.id,
								);
							});
						} catch (err) {
							console.log('Error creating deleted deal doc', err);
						}
					} catch (err) {
						console.log('Error creating activity doc', err);
					}

					try {
						dispatch(
							clientsApi.util.invalidateTags([
								'CLIENT_POLICIES',
								'CLIENT',
								'CLIENTS',
							]),
						);
						dispatch(engagementsApi.util.invalidateTags(['ENGAGEMENTS']));
						dispatch(
							api.util.invalidateTags([
								{ type: uppercaseType, id: dealId },
								`${uppercaseType}`,
							]),
						);
						setTimeout(async () => {
							dispatch(triggerRefresh());
						}, 1000);
					} catch (error) {
						console.log('Error invalidating tags', error);
					}
				} catch (error) {
					console.log('Error deleting deal', dealId);
					return { data: 'error' };
				}
				return { data: 'success' };
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform deleteDeal response', response);
				return response;
			},
			onQueryStarted: async (
				{ dealId, objectType },
				{ dispatch, queryFulfilled, getState },
			) => {
				const state = getState();

				const uppercaseType = (objectType ?? '').toUpperCase();
				const api = getApi(uppercaseType);
				try {
					await queryFulfilled;
					try {
						dispatch(
							clientsApi.util.invalidateTags([
								'CLIENT_POLICIES',
								'CLIENT',
								'CLIENTS',
							]),
						);
						dispatch(
							api.util.invalidateTags([
								{ type: uppercaseType, id: dealId },
								`${uppercaseType}`,
							]),
						);
						setTimeout(async () => {
							dispatch(triggerRefresh());
						}, 1000);
					} catch (error) {
						console.log('Error invalidating tags', error);
					}
				} catch (error) {
					console.log('Error deleting deal', dealId);
				}
			},
		}),
		refreshDealPolicies: builder.mutation({
			invalidatesTags: (result, error, { dealIds }) => [
				{ type: 'DEALS', id: 'LIST' },
				...dealIds.flatMap((dealId) => [
					{ type: 'DEAL', id: dealId },
					{ type: 'DEAL_ROW', id: dealId },
				]),
				{ type: 'CLIENT_DEALS', id: 'LIST' },
			],
			queryFn: async ({ dealIds = [], clientId }, { getState, dispatch }) => {
				console.log('Params', { dealIds, clientId });
				const userData = await getActingUser(dispatch, getState);

				const brokerGroupArray = userData?.brokerGroupArray ?? [];
				// One query per deal: combining `dealId in` with the `brokerGroupId in` that
				// queryByBrokerGroup adds passes Firestore's 30-value limit for users in 4+
				// broker groups.
				const policyDocsByDeal = await Promise.all(
					dealIds.map((dealId) =>
						queryByBrokerGroup({
							collectionRef: collection(firestore, 'policies'),
							brokerGroupIds: brokerGroupArray,
							constraints: [where('dealId', '==', dealId)],
						}),
					),
				);
				const policies = policyDocsByDeal.flat();
				const policyBatches = chunk(policies, 500);

				for (const policyChunk of policyBatches) {
					const batch = writeBatch(firestore);
					policyChunk.forEach((item) => {
						batch.update(item.ref, { forceUpdate: true });
					});

					await batch
						.commit()
						.catch((e) => console.log('Error updating policy batch', e));

					if (policyBatches.length > 1) {
						await delay(100);
					}
				}
				try {
					dispatch(
						clientsApi.util.invalidateTags([
							{ type: 'CLIENTS', id: 'LIST' },
							...(clientId
								? [
										{ type: 'CLIENT', id: clientId },
										{ type: 'CLIENT_POLICIES', id: clientId },
									]
								: []),
						]),
					);
				} catch (e) {
					console.log('Error invalidating client tags', e);
				}
				return {
					data: {
						success: true,
						policies: policies.map((p) => ({
							docId: p.id,
							policyId: p.data()?.policyId,
							policyTransactionId: p.data()?.policyTransactionId,
						})),
					},
				};
			},
		}),
		listDeals: builder.query({
			providesTags: (result, error, arg) => [{ type: 'DEALS', id: arg.filters }],
			query: ({ filters, includeFields }) => ({
				url: `${process.env.REACT_APP_DEAL_API}/list`,
				method: 'POST',
				body: { includeFields, filters },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform listDeals error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform listDeals response', response);
				return response;
			},
		}),
		//Need to add archived tab?
		getFirebaseDealCount: builder.query({
			providesTags: (result, error, arg) => [{ type: 'DEALS', id: 'LIST' }],
			queryFn: async (
				{
					filters = {},
					countType = 'inProgressDealAmount',
					userIds,
					hideArchived = true,
					dealStages,
					pipelineId,
					conversionRates,
				},
				{ dispatch, getState },
				options,
				baseQuery,
			) => {
				if (!Array.isArray(userIds) || userIds.length === 0) {
					return { data: [], countTotal: 0 };
				}

				console.log('filters: ', filters);

				try {
					const ownerIds = userIds.filter((id) => id != null && id !== '');

					//Try and add a query here if dealStage filter has been selected
					const userData = await getActingUser(dispatch, getState);

					const brokerGroupArray = userData?.brokerGroupArray ?? [];

					const pipelineSchema = `pipeline := ${pipelineId}`;
					const ownerIdsSchema = `ownerDocId: [${ownerIds.join(',')}]`;
					const brokerGroupIdsSchema = `brokerGroupId: [${brokerGroupArray.join(',')}]`;

					// Use the stage list from the card when it is set.
					const dealStageSchema =
						(dealStages ?? []).length > 0
							? ` && dealStage: [${dealStages.join(',')}]`
							: Array.isArray(filters?.dealStage) &&
								  filters?.dealStage?.length !== 0 &&
								  countType !== 'invoicedDealAmount' &&
								  countType !== 'totalDealAmount'
								? ` && dealStage: [${filters.dealStage.join(',')}]`
								: '';

					const filterEntries = Object.entries(filters)
						.filter(([_, values]) => {
							if (!Array.isArray(values) || values.length === 0) return false;
							const filteredValues = values.filter((v) => v !== '');
							if (filteredValues.length === 0) return false;
							return true;
						})
						.map(([_, values]) => values.filter((v) => v !== ''))
						.reduce((acc, [key, values]) => {
							switch (key) {
								case 'createDate': {
									if (
										countType === 'totalDealAmount' ||
										countType === 'conversionDealAmount'
									) {
										break;
									}
									const rangeRegex = /createDate:\[(\d+)\.\.(\d+)\]/g;
									const clauses = values.flatMap((filter) => {
										const cleaned = String(filter)
											.replace(/^&&\s*\(/, '')
											.replace(/\)$/, '');

										const ranges = [...cleaned.matchAll(rangeRegex)];

										return ranges.map((match) => {
											const start = Number(match[1]);
											const end = Number(match[2]);
											return `(createDate:>=${start} && createDate:<=${end})`;
										});
									});

									if (clauses.length === 1) {
										acc.push(clauses[0]);
									} else if (clauses.length > 1) {
										acc.push(`(${clauses.join(' || ')})`);
									}
									break;
								}
								case 'estimatedCloseDate': {
									if (
										countType === 'totalDealAmount' ||
										countType === 'conversionDealAmount'
									) {
										break;
									}
									const rangeRegex = /estimatedCloseDate:\[(\d+)\.\.(\d+)\]/g;
									const clauses = values.flatMap((filter) => {
										const cleaned = String(filter)
											.replace(/^&&\s*\(/, '')
											.replace(/\)$/, '');

										const ranges = [...cleaned.matchAll(rangeRegex)];

										return ranges.map((match) => {
											const start = Number(match[1]);
											const end = Number(match[2]);
											return `(estimatedCloseDate:>=${start} && estimatedCloseDate:<=${end})`;
										});
									});
									if (clauses.length === 1) {
										acc.push(clauses[0]);
									} else if (clauses.length > 1) {
										acc.push(`(${clauses.join(' || ')})`);
									}
									break;
								}
								case 'isProspect': {
									// Only filter if there are any filters

									const clauses = values
										.map((filter) => {
											const match = String(filter).match(
												/isProspect:(=|!=)\s*(true|false)/,
											);
											if (!match) return null;

											const operator = match[1];
											const value = match[2];

											return `isProspect:${operator}${value}`;
										})
										.filter(Boolean);

									if (clauses.length === 1) {
										acc.push(clauses[0]);
									} else if (clauses.length > 1) {
										acc.push(`(${clauses.join(' || ')})`);
									}
									break;
								}

								default: {
									break;
								}
							}
							return acc;
						}, []);

					const baseSchema = [
						`${pipelineSchema} && ${hideArchived ? `status:!=${DEAL_STATUS_TYPES.archived} && ` : ''}(${ownerIdsSchema} && ${brokerGroupIdsSchema})${dealStageSchema}`,
					];
					const filterSchema = [...baseSchema, ...filterEntries].join(' && ');

					const q = await baseQuery({
						url: `${process.env.REACT_APP_DEAL_API}/list`,
						method: 'POST',
						body: { filters: filterSchema },
					});
					if (q.error) return { error: q.error };

					console.log('Get firebase deal count res', q);

					const docs = q.data;

					//Need to add countType conditions in the switch case so that it doesn't filter by e.g createDate or estimatedCLosed for total deal amount

					//Need to calculate averages maybe there is a max func

					// for (const [key, values] of Object.entries(filters)) {
					// 	if (!Array.isArray(values) || values.length === 0) continue;
					// 	const filteredValues = values.filter((v) => v !== '');
					// 	// console.log("values: ", filteredValues);
					// 	if (filteredValues.length === 0) continue;
					// 	// console.log("key: ", key);

					// 	switch (key) {
					// 		case 'createDate': {
					// 			const rangeRegex = /createDate:\[(\d+)\.\.(\d+)\]/g;
					// 			docs = docs.filter((d) => {
					// 				if (!d.createDate) return false;
					// 				return filteredValues.some((filter) => {
					// 					filter = filter.replace(/^&&\s*\(/, '').replace(/\)$/, '');
					// 					const ranges = [...filter.matchAll(rangeRegex)];
					// 					return ranges.some((rangeMatch) => {
					// 						const start = Number(rangeMatch[1]);
					// 						const end = Number(rangeMatch[2]);
					// 						return d.createDate >= start && d.createDate <= end;
					// 					});
					// 				});
					// 			});
					// 			break;
					// 		}
					// 		case 'estimatedCloseDate': {
					// 			const rangeRegex = /estimatedCloseDate:\[(\d+)\.\.(\d+)\]/g;
					// 			docs = docs.filter((d) => {
					// 				if (!d.estimatedCloseDate) return false;
					// 				return filteredValues.some((filter) => {
					// 					filter = filter.replace(/^&&\s*\(/, '').replace(/\)$/, '');
					// 					const ranges = [...filter.matchAll(rangeRegex)];
					// 					const dealEstimatedDate = d.estimatedCloseDate.seconds;
					// 					return ranges.some((rangeMatch) => {
					// 						const start = Number(rangeMatch[1]);
					// 						const end = Number(rangeMatch[2]);
					// 						return (
					// 							dealEstimatedDate >= start &&
					// 							dealEstimatedDate <= end
					// 						);
					// 					});
					// 				});
					// 			});
					// 			break;
					// 		}
					// 		case 'isProspect': {
					// 			// Only filter if there are any filters
					// 			if (filteredValues.length > 0) {
					// 				docs = docs.filter((d) => {
					// 					return filteredValues.some((filter) => {
					// 						const match = filter.match(
					// 							/isProspect:(=|!=)\s*(true|false)/,
					// 						);
					// 						if (!match) return false;
					// 						const operator = match[1];
					// 						const value = match[2] === 'true';
					// 						if (operator === '=') {
					// 							return d.isProspect === value;
					// 						} else if (operator === '!=') {
					// 							return d.isProspect !== value;
					// 						}
					// 					});
					// 				});
					// 			}
					// 			break;
					// 		}

					// 		default: {
					// 			console.log(`Not adding filter: ${key}...`);
					// 		}
					// 	}
					// }
					let countTotal = 0;
					let countAverage = 0;
					const avgDocArray = docs.filter((d) => d.dealAmount != null);

					if (
						countType === 'totalDealAmount' ||
						countType === 'inProgressDealAmount' ||
						countType === 'invoicedDealAmount'
					) {
						const totalAmount = docs.reduce((sum, h) => {
							const amount = Number(h.dealAmount) || 0;
							return sum + amount;
						}, 0);

						countTotal = formatCurrency({ num: totalAmount });
						const avgAmount =
							avgDocArray.length > 0 ? totalAmount / avgDocArray.length : 0;
						countAverage = formatCurrency({ num: avgAmount });
					} else if (countType === 'conversionDealAmount') {
						// console.log('avg doc array: ', avgDocArray);

						const validDeals = docs.filter((deal) => deal.dealAmount != null);
						const totalConversionAmount = validDeals.reduce((sum, deal) => {
							const rate = Number(conversionRates[deal.dealStage] ?? 0);
							// console.log('dealStage: ', deal.dealStage, 'rate: ', rate);
							return sum + deal.dealAmount * (rate / 100);
						}, 0);

						countTotal = formatCurrency({ num: totalConversionAmount });

						const avgConversionAmount =
							validDeals.length > 0 ? totalConversionAmount / validDeals.length : 0;
						countAverage = formatCurrency({ num: avgConversionAmount });
					} else {
						countTotal = 0;
					}
					return { data: { countTotal, countAverage } };
				} catch (err) {
					console.error('Error fetching deals:', err);
					return { error: { status: 500, data: err.message } };
				}
			},
		}),
		//For now just adding the api /create whole endpoint here could potentially add to the base query but skeptical for now
		createDebtor: builder.mutation({
			// This tag indicates that this mutation invalidates the list of clients
			invalidatesTags: (result, error, arg) => [
				{ type: 'DEALS', id: 'LIST' },
				'CLIENT_DEALS',
			],
			query: ({
				ownerDocId,
				dealName,
				notes,
				clientId,
				completedBy,
				yearFolder,
				transactions,
			}) => ({
				url: `${process.env.REACT_APP_DEBTOR_API}/create`,
				method: 'POST',
				body: {
					ownerDocId,
					dealName,
					notes,
					clientId,
					completedBy,
					yearFolder,
					transactions,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform create Debtor response', response);
				return response;
			},
			onQueryStarted: async ({ ownerDocId }, { dispatch, getState, queryFulfilled }) => {
				try {
					const res = await queryFulfilled;
					const dealId = res.data?.dealId;
					if (dealId) {
						await createDealAssignedNotification({ dispatch, getState, dealId, assigneeDocId: ownerDocId });
					}
				} catch (e) {
					//
				}
			},
		}),
		batchGetDeals: builder.query({
			queryFn: async (dealIds, { dispatch, getState }) => {
				if (!dealIds.length) return { data: [] };

				const dataByDealId = getDealsFromCache(getState(), dealIds);

				const missingFromCache = dealIds.filter((id) => !dataByDealId.has(id));
				if (missingFromCache.length) {
					const fromTypesense = await getDealsFromTypesense(missingFromCache, dispatch);
					for (const [id, data] of fromTypesense) dataByDealId.set(id, data);
				}

				const missingFromTypesense = dealIds.filter((id) => !dataByDealId.has(id));
				if (missingFromTypesense.length) {
					const fromRtk = await getDealsFromRtk(missingFromTypesense, dispatch);
					for (const [id, data] of fromRtk) dataByDealId.set(id, data);
				}

				return { data: Array.from(dataByDealId.values()) };
			},
		}),
		generatePortfolioStatement: builder.mutation({
			invalidatesTags: (result, error, { dealId }) => [
				{ type: 'PORTFOLIO_STATEMENTS', id: dealId },
			],
			query: ({ dealId, policyIds }) => ({
				url: `${process.env.REACT_APP_DEAL_API}/${dealId}/portfolioStatements/generate`,
				method: 'POST',
				body: { dealId, policyIds },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform generatePortfolioStatement error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform generatePortfolioStatement response', response);
				return response;
			},
		}),
		updatePortfolioStatement: builder.mutation({
			invalidatesTags: (result, error, { dealId }) => [
				{ type: 'PORTFOLIO_STATEMENTS', id: dealId },
			],
			query: ({ dealId, docId, status }) => ({
				url: `${process.env.REACT_APP_DEAL_API}/${dealId}/portfolioStatements/update`,
				method: 'POST',
				body: { dealId, docId, status },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform updatePortfolioStatement error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform updatePortfolioStatement response', response);
				return response;
			},
		}),
		listPortfolioStatements: builder.query({
			providesTags: (result, error, { dealId }) => [
				{ type: 'PORTFOLIO_STATEMENTS', id: dealId },
			],
			query: ({ dealId, status, latest }) => ({
				url: `${process.env.REACT_APP_DEAL_API}/${dealId}/portfolioStatements/list`,
				method: 'POST',
				body: { dealId, status, latest },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform listPortfolioStatements error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform listPortfolioStatements response', response);
				return response?.portfolioStatements ?? [];
			},
		}),
	}),
});

// Exporting auto-generated hooks for the defined endpoints
export const {
	useGetDealPolicyTransactionsQuery,
	useBatchGetDealListPoliciesQuery,
	useGetDealRowQuery,
	useLazyGetDealRowQuery,
	useListDealsQuery,
	useLazyListDealsQuery,
	useDeleteDealMutation,
	useAddPoliciesToDealMutation,
	useGetDealPoliciesQuery,
	useLazyGetDealPoliciesQuery,
	useCreateNewBusinessMutation,
	useCreateEndorsementMutation,
	useCreateRenewalMutation,
	usePreviewBulkRenewalsMutation,
	useCreateBulkRenewalsMutation,
	useBatchUpdateDealsMutation,
	usePreviewBulkDebtorsMutation,
	useCreateBulkDebtorsMutation,
	useUpdateDealRowMutation,
	useUpdateClientDealRowsMutation,
	useGetFolioEngageShareLinksQuery,
	useGetEngageReportsQuery,
	useGenerateEngageReportMutation,
	useUpdateEngageReportMutation,
	useBatchUpdateDealRowsMutation,
	useRemoveDealPolicyMutation,
	useUpdateDealPolicyTransactionMutation,
	useUpdateDealPoliciesMutation,
	useRefreshDealPoliciesMutation,
	useGetFirebaseDealCountQuery,
	useGetDealsByClientIdQuery,
	useCreateDebtorMutation,
	useLazyBatchGetDealListPoliciesQuery,
	useBatchGetDealsQuery,
	useLazyBatchGetDealsQuery,
	useGeneratePortfolioStatementMutation,
	useUpdatePortfolioStatementMutation,
	useListPortfolioStatementsQuery,
	useLazyListPortfolioStatementsQuery,
} = dealsApi;
